<script lang="ts" setup>
const { cart, errors, applyCouponToCart, removeCouponFromCart, pending } = useCart()
const { $t } = useI18n()
const { show: showNotification } = useNotification()
const couponCode = ref<string>('')
const error = ref<string>('')
const open = ref<boolean>(false)
const editing = ref<boolean>(false)
const coupon = computed(() =>
  cart.value?.applied_coupons?.[0] || null,
)

const apply = async () => {
  editing.value = true
  if (!couponCode.value) {
    error.value = i18n.t('Please enter a discount code')
    editing.value = false
    return
  }

  await applyCouponToCart(couponCode.value)
  if (errors.value.length) {
    error.value = errors.value?.[0] || ''
  }
  else {
    error.value = ''
  }
  editing.value = false

  if (coupon.value) {
    showNotification({
      type: 'success',
      message: $t('The discount code is applied to your cart'),
    })
  }
}

const remove = async () => {
  editing.value = true
  if (!coupon.value?.code) {
    error.value = $t('Error removing discount code')
    editing.value = false
    return
  }

  await removeCouponFromCart()
  if (errors.value.length) {
    error.value = errors.value?.[0] || ''
  }
  else {
    error.value = ''
  }
  editing.value = false

  if (!coupon.value.code) {
    showNotification({
      type: 'info',
      message: $t('The discount code code is removed from your cart'),
    })
  }
}

function toggleForm() {
  open.value = !open.value
}
</script>

<template>
  <div class="coupon">
    <div
      v-if="coupon"
      class="coupon__view"
    >
      <div class="coupon__view-heading">
        {{ $t('Applied discount code') }}
      </div>

      <div class="coupon__view-item">
        <button
          type="button"
          class="coupon__remove"
          :disabled="pending"
          @click.prevent="remove()"
        >
          <strong>
            {{ coupon.code }}
          </strong>

          <nuxtIcon name="icon:close" />
        </button>
      </div>
    </div>
    <div
      v-else
      class="coupon__inner coupon__apply-view"
      :class="{ open: open }"
    >
      <button
        type="button"
        class="coupon__toggle"
        @click="toggleForm()"
      >
        <span>{{ $t('Add coupon code') }}</span>
        <nuxtIcon :name="!open ? 'icon:plus' : 'icon:min'" />
      </button>

      <div class="coupon__form">
        <form @submit.prevent="apply">
          <input
            v-model="couponCode"
            type="text"
            :disabled="pending"
            :placeholder="$t('Enter coupon code')"
            :class="{ 'form__input--error': error }"
          >

          <p
            v-if="error"
            class="coupon__message message message--error"
          >
            {{ error }}
          </p>

          <button
            type="submit"
            :disabled="pending"
            class="coupon__button"
          >
            <span>{{ $t('Apply coupon code') }}</span>
            <NuxtIcon
              v-if="editing"
              name="icon:loader"
            />
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<style>
@import url("~layers/components/Cart/Coupon.css");
</style>
